import iyinPhoto from "@/public/assets/img/iyin.png"
import odunPhoto from "@/public/assets/img/odun.png"
import uzomaPhoto from "@/public/assets/img/uzoma.png"
import yemiPhoto from "@/public/assets/img/yemi.png"
import SidebarMenuItemsMockV2 from "@/src/__mocks__/api/SidebarMenuItemsMockV2"
import { URLFactory } from "@/src/sb/business-layer/factories/URLFactory"
import { LicenceType } from "@/src/sb/business-layer/types"
import {
  ENERGY_PRICE_TRACKER_SLUG,
  V2_FEATURE_TOGGLES
} from "@/src/sb/constants/configs"
import { pages } from "@/src/sb/constants/copy"
import {
  ARTICLE_V3,
  AVAILABLE_COUNTRY_RISK_SLUGS,
  DEFAULT_HOME_PAGE,
  EXTRA_SIDEBAR_ITEMS,
  EXTRA_SIDEBAR_TOP_ITEMS,
  FeatureFlag,
  IS_WEBFLOW_COUNTRY_RISK_PATH,
  SIDEBAR_COUNTRIES_CLASS_NAME,
  SIDEBAR_INDUSTRIES_CLASS_NAME,
  SIDEBAR_MAX_DEPTH,
  USE_V2_TAG_CLASSIFICATION
} from "@/src/sb/constants/featureFlags"
import { QueryHookOptions } from "@apollo/client"

export const SUBSCRIPTIONS_PAGE_FAQs = [
  {
    question: "What is Stears Premium?",
    answer: `
      <p>Stears Premium refers to our digital-only subscription service. All Stears Premium content is created by our in-house analysts and focuses on specific industries, issues, themes and companies. Each weekday morning, subscribers will receive an 8am email from our editors introducing that day’s Stears Premium story and why it is important for you.</p>

      <p>To learn more about Stears Premium, please <a href="https://www.stearsng.com/article/stears-premium-information-worth-paying-for" class="text-accent-600">read this message from our CEO</a>.</p>
      
      <div>
        <p>You can access Stears Premium using</p>
        <ul class="list-disc list-inside ml-4">
          <li>
            Personal subscription for individuals
          </li>
          <li>
            Corporate licence for companies and organisations
          </li>
        </ul>
      </div>
      `
  },
  {
    question: "How can I get a personal Stears subscription?",
    // language=HTML
    answer: `
      <div>
        <p>To get a personal Stears subscription, you need to sign up for any of our premium subscription plans. You can
          subscribe to any of these three options: </p>
        <ul class="list-disc list-inside ml-4">
          <li>
            Monthly plan
          </li>
          <li>
            Quarterly plan
          </li>
          <li>
            Annual Plan
          </li>
        </ul>
      </div>

      <p>To get your Stears subscription, <a href="${pages.subscriptions}?position=faq" class="text-accent-600">click
        here</a></p>

      <p>If you have any questions or need help, please contact <a href="mailto:support@stears.co"
                                                                   class="text-accent-600">support@stears.co</a></p>
    `
  },
  {
    question: "How can I get a corporate licence?",
    answer: `
      <p>Companies, organisations and schools that want firm-wide access to Stears Premium can purchase a corporate licence.  </p>

      <div>
        <p>Corporate licences provide team-wide access to all employees within an organisation, for 1 year. Our corporate licence gives you: </p>
        <ul class="list-disc list-inside ml-4">
          <li>
            Access to a rich library of data-driven insights
          </li>
          <li>
            Access to proprietary sector data-trackers and indices
          </li>
          <li>
            Invitation to subscriber-only events
          </li>
          <li>
            Quarterly sector-focused briefings & presentations directly from analysts
          </li>
          <li>
            Access to Stears’ proprietary insights database
          </li>
        </ul>
      </div>
      
      <p>To learn more about our corporate licence, please fill this <a href="https://share.hsforms.com/13ObtRbV5RMS-Kut-3vvj4g4uj82" class="text-accent-600">form</a>. Our sales team will get in touch with you.</p>
    `
  },
  {
    question: "What forms of payment do you accept?",
    answer: `
      <p>For personal subscriptions, we accept most international and local debit cards. For your convenience and the best experience, we recommend card payment. </p>
      <p>For corporate licences, we accept bank transfers. </p>
      
      <p>If you are having trouble with payment, please email <a href="mailto:support@stears.co" class="text-accent-600">support@stears.co</a>  for assistance. </p>
    `
  },
  {
    question: "How can I cancel my Stears subscription?",
    answer: `
      <p>You can cancel your subscription at any time on your dashboard. You will not be billed for any subsequent subscriptions.</p>

      <p>Although you may cancel, your access to Stears Premium content will remain active. The cancellation will only take effect at the end of your then-current subscription period.</p>
      
      <p>Please note that the option to cancel a Stears subscription is only available to subscribers that paid with their cards.</p>
      
      <p>If you have any additional questions, please email us at <a href="mailto:support@stears.co" class="text-accent-600">support@stears.co</a>.</p>
    `
  },
  {
    question: "Do you have a mobile app?",
    answer: `
      <p>Yes, Stears has a mobile app, and it is available on both iOS and Android devices. Our mobile app makes it easy for you to search and discover content that is right for you.</p>

      <p>To start enjoying our intuitive mobile app, please download it <a href="https://onelink.to/uu49gu" class="text-accent-600">here</a></p>
    `
  }
]

export const SUBSCRIPTIONS_PAGE_TESTIMONIALS = [
  {
    image: odunPhoto,
    name: "Odun Eweniyi",
    work: "Co-founder & COO PiggyVest",
    text: "“Stears has bridged the gap between high-quality reporting on business, policy and the economy and incredible simplicity in passing information across. No one is doing it like them.”"
  },
  {
    image: yemiPhoto,
    name: "Dr. Yemi Kale",
    work: "Former Statistician-General, Nigerian Bureau of Statistics",
    text: "“Stears is one of my preferred and regular sources of high-quality insight on policy and macroeconomic development.”"
  },
  {
    image: uzomaPhoto,
    name: "Uzoma Dozie",
    work: "CEO Sparkle. Former CEO, Diamond Bank",
    text: "“I love Stears. Relevant, readable, and educational material for leaders who are starting, growing, or managing a business.”"
  },
  {
    image: iyinPhoto,
    name: "Iyin Aboyeji",
    work: "Co-founder at Andela & GP at Future Africa",
    text: "“Stears is Africa’s Bloomberg. Period”"
  }
]

export const SUBSCRIPTIONS_PAGE_SUBSCRIBERS = [
  "unnamed.png",
  "pz-cussons.png",
  "infra-credit.png",
  "sterling.png",
  "nigeria-brewries.png",
  "udpn.png",
  "piggyvest.png",
  "paystack.png",
  "nestle.png",
  "luminate.png",
  "pac-holdings.png",
  "business-france.png",
  "future-africa.png",
  "buycoins.png",
  "mtn.png",
  "heineken.png",
  "africa-practice.png",
  "ihs.png"
]

export const SUBSCRIPTIONS_PAGE_SUBSCRIBERS_DARK_THEME = [
  "aca.png",
  "lightrock.png",
  "reckitt.png",
  "european-investment-bank.png",
  "bank-of-industry.png",
  "nsid.png"
]

export const HIDE_ARTICLE_PREMIUM_UPGRADE_BANNER =
  "hide-article-premium-upgrade-banner" // cleared on session expiry
export const HIDE_INTELLIGENCE_SOLUTIONS_BANNER =
  "hide-intelligence-solutions-banner" // cleared on session expiry

export const ENERGY_PRICE_TRACKER_OBJECT = {
  name: "Energy Price Tracker",
  href: URLFactory.report(ENERGY_PRICE_TRACKER_SLUG, false),
  slug: ENERGY_PRICE_TRACKER_SLUG
}

export const DATA_TRACKER = "data-tracker"
export const MATCH_TO_SHOW_INTELLIGENCE_SOLUTIONS_BANNER = [
  /^\/topics\/.+/i,
  /^\/home\/?$/i
]

export const MATCH_TO_IGNORE_INTELLIGENCE_SOLUTIONS_BANNER_PLATFORM = [
  /^\/search\/\?q=.+/i
]

MATCH_TO_SHOW_INTELLIGENCE_SOLUTIONS_BANNER.push(
  ...(V2_FEATURE_TOGGLES.has(ARTICLE_V3)
    ? []
    : [/^\/article\/.+/i, /^\/premium\/article\/.+/i])
)

export const DATA_PAGE_AUTHORIZED_LICENCE_TIERS = [
  LicenceType.INVESTOR,
  LicenceType.PRO,
  LicenceType.STRATEGY
]

export const COMMON_PROPS_USE_CASE_FEATURES: Array<
  FeatureFlag | [FeatureFlag, any]
> = [
  USE_V2_TAG_CLASSIFICATION,
  [EXTRA_SIDEBAR_TOP_ITEMS, {}],
  [SIDEBAR_COUNTRIES_CLASS_NAME, "Countries"],
  [SIDEBAR_INDUSTRIES_CLASS_NAME, "Sectors"],
  [EXTRA_SIDEBAR_ITEMS, SidebarMenuItemsMockV2()],
  [SIDEBAR_MAX_DEPTH, 2],
  [AVAILABLE_COUNTRY_RISK_SLUGS, ""],
  [DEFAULT_HOME_PAGE, pages.home],
  [IS_WEBFLOW_COUNTRY_RISK_PATH, false]
]

interface IFieldConfig {
  queryKeys: string[]
  dataPaths: string[]
  label: string
  type: "string" | "number" | "date" | "array" | "boolean"
  isRange?: boolean
  appliesTo: ("recent_transactions" | "companies" | "limited_partners")[]
}

const SCREENER_FIELD_CONFIG: Record<string, IFieldConfig> = {
  companySlug: {
    queryKeys: ["company.slug[]"],
    dataPaths: ["company.slug"],
    label: "Target",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  companyTagSlug: {
    queryKeys: ["company.tag.slug[]"],
    dataPaths: ["company.tag.slug"],
    label: "Subsector",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  countryOfOperation: {
    queryKeys: ["countryOfOperation.slug[]"],
    dataPaths: ["countryOfOperation.slug"],
    label: "Countries of Operation",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  companyMarketSlug: {
    queryKeys: ["market.slug[]"],
    dataPaths: ["market.slug"],
    label: "Market",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  companySectorSlug: {
    queryKeys: ["sector.slug[]"],
    dataPaths: ["sector.slug"],
    label: "Sector",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  ownershipStatus: {
    queryKeys: ["company.ownershipStatus[]"],
    dataPaths: ["company.ownershipStatus"],
    label: "Ownership Status",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  status: {
    queryKeys: ["company.status[]"],
    dataPaths: ["company.status"],
    label: "Company Status",
    type: "array",
    appliesTo: ["companies"]
  },
  dealSize: {
    queryKeys: ["deal.dealSize.min", "deal.dealSize.max"],
    dataPaths: ["deal.dealSize"],
    label: "Transaction Value ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions"]
  },
  equityStake: {
    queryKeys: [
      "deal.percentageEquityStake.min",
      "deal.percentageEquityStake.max"
    ],
    dataPaths: ["deal.percentageEquityStake"],
    label: "Equity Stake (%)",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions"]
  },
  impliedEv: {
    queryKeys: ["deal.impliedEv.min", "deal.impliedEv.max"],
    dataPaths: ["deal.impliedEv"],
    label: "Implied EV ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions"]
  },
  impliedEvRevenue: {
    queryKeys: ["deal.impliedEvRevenue.min", "deal.impliedEvRevenue.max"],
    dataPaths: ["deal.impliedEvRevenue"],
    label: "Implied EV/Revenue",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions"]
  },
  impliedEvEbitda: {
    queryKeys: ["deal.impliedEvEbitda.min", "deal.impliedEvEbitda.max"],
    dataPaths: ["deal.impliedEvEbitda"],
    label: "Implied EV/EBITDA",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions"]
  },
  announcementDate: {
    queryKeys: [
      "deal.announcementDate.predefinedOperation",
      "deal.announcementDate.operationName",
      "deal.announcementDate.start",
      "deal.announcementDate.end"
    ],
    dataPaths: [
      "deal.announcementDate",
      "deal.announcementDate.predefinedOperation",
      "deal.announcementDate.operationName",
      "deal.announcementDate.start",
      "deal.announcementDate.end"
    ],
    label: "Announcement Date",
    type: "date",
    appliesTo: ["recent_transactions"]
  },
  investmentRound: {
    queryKeys: [],
    dataPaths: ["deal.investmentRound"],
    label: "Investment Round",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  fund: {
    queryKeys: ["deal.fund[]"],
    dataPaths: ["deal.fund"],
    label: "Fund",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  ebitda: {
    queryKeys: [
      "financial.ebitda.min",
      "financial.ebitda.max",
      "financial.ebitda.type"
    ],
    dataPaths: ["financial.ebitda"],
    label: "EBITDA ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions", "companies"]
  },
  revenue: {
    queryKeys: [
      "financial.revenue.min",
      "financial.revenue.max",
      "financial.revenue.type"
    ],
    dataPaths: ["financial.revenue"],
    label: "Revenue ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["recent_transactions", "companies"]
  },
  investor: {
    queryKeys: ["investor.slug[]"],
    dataPaths: ["investor.slug"],
    label: "Buyers/Investors",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  leadInvestor: {
    queryKeys: ["leadInvestor.slug[]"],
    dataPaths: ["leadInvestor.slug"],
    label: "Lead Investor/Buyer",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  subsector: {
    queryKeys: ["subsector.slug[]"],
    dataPaths: ["subsector.slug"],
    label: "Subsector",
    type: "array",
    appliesTo: ["recent_transactions", "companies"]
  },
  investmentType: {
    queryKeys: [],
    dataPaths: ["investmentType.slug"],
    label: "Investment Type",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  transactionType: {
    queryKeys: ["transactionType.slug[]"],
    dataPaths: ["transactionType.slug"],
    label: "Transaction Type",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  investmentStrategy: {
    queryKeys: ["investmentStrategy.slug[]"],
    dataPaths: ["investmentStrategy.slug"],
    label: "Investment Strategy",
    type: "array",
    appliesTo: ["recent_transactions", "limited_partners"]
  },
  financingInstrument: {
    queryKeys: ["financingInstrument.slug[]"],
    dataPaths: ["financingInstrument.slug"],
    label: "Financing Instrument",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  vcFundingRound: {
    queryKeys: ["vcFundingRound.slug[]"],
    dataPaths: ["vcFundingRound.slug"],
    label: "VC Funding Rounds",
    type: "array",
    appliesTo: ["recent_transactions"]
  },
  operatingExpenses: {
    queryKeys: [
      "financial.operatingExpenses.min",
      "financial.operatingExpenses.max"
    ],
    dataPaths: ["financial.operatingExpenses"],
    label: "Operating Expenses",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  priceToEarningRatio: {
    queryKeys: [
      "financial.percentagePriceToEarningRatio.min",
      "financial.percentagePriceToEarningRatio.max"
    ],
    dataPaths: ["financial.percentagePriceToEarningRatio"],
    label: "Price/Earning Ratio ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  priceToBookRatio: {
    queryKeys: [
      "financial.priceToBookRatio.min",
      "financial.priceToBookRatio.max"
    ],
    dataPaths: ["financial.priceToBookRatio"],
    label: "Price/Book Ratio($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  evSales: {
    queryKeys: ["financial.evSales.min", "financial.evSales.max"],
    dataPaths: ["financial.evSales"],
    label: "EV/Sales",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  netProfitMargin: {
    queryKeys: [
      "financial.percentageNetProfitMargin.min",
      "financial.percentageNetProfitMargin.max"
    ],
    dataPaths: ["financial.percentageNetProfitMargin"],
    label: "Net Profit Margin (%)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  returnOnEquity: {
    queryKeys: [
      "financial.percentageReturnOnEquity.min",
      "financial.percentageReturnOnEquity.max"
    ],
    dataPaths: ["financial.percentageReturnOnEquity"],
    label: "Return on Equity (%)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  currentRatio: {
    queryKeys: ["financial.currentRatio.min", "financial.currentRatio.max"],
    dataPaths: ["financial.currentRatio"],
    label: "Current Ratio",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  debtToEquityRatio: {
    queryKeys: [
      "financial.debtToEquityRatio.min",
      "financial.debtToEquityRatio.max"
    ],
    dataPaths: ["financial.debtToEquityRatio"],
    label: "Debt to Equity Ratio (%)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  totalAmountRaised: {
    queryKeys: [
      "transactionHistory.totalAmountRaised.min",
      "transactionHistory.totalAmountRaised.max",
      "transactionHistory.totalAmountRaised.type"
    ],
    dataPaths: ["transactionHistory.totalAmountRaised"],
    label: "Total Amount Raised ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  lastTransactionAmount: {
    queryKeys: [
      "transactionHistory.lastTransactionAmount.min",
      "transactionHistory.lastTransactionAmount.max",
      "transactionHistory.lastTransactionAmount.type"
    ],
    dataPaths: ["transactionHistory.lastTransactionAmount"],
    label: "Latest Transaction Amount Raised ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  lastPreMoneyValuation: {
    queryKeys: [
      "transactionHistory.lastPreMoneyValuation.min",
      "transactionHistory.lastPreMoneyValuation.max",
      "transactionHistory.lastPreMoneyValuation.type"
    ],
    dataPaths: ["transactionHistory.lastPreMoneyValuation"],
    label: "Latest Pre-Money Valuation ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  lastPostMoneyValuation: {
    queryKeys: [
      "transactionHistory.lastPostMoneyValuation.min",
      "transactionHistory.lastPostMoneyValuation.max",
      "transactionHistory.lastPostMoneyValuation.type"
    ],
    dataPaths: ["transactionHistory.lastPostMoneyValuation"],
    label: "Latest Post-Money Valuation ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["companies"]
  },
  foundingYear: {
    queryKeys: [
      "foundingYear.predefinedOperation",
      "foundingYear.selectedOption",
      "foundingYear.operationName",
      "foundingYear.min",
      "foundingYear.max"
    ],
    dataPaths: [
      "foundingYear",
      "foundingYear.predefinedOperation",
      "foundingYear.operationName",
      "foundingYear.start",
      "foundingYear.end",
      "foundingYear.min",
      "foundingYear.max"
    ],
    label: "Founding Year",
    type: "number",
    isRange: true,
    appliesTo: ["companies", "limited_partners"]
  },
  currentInvestors: {
    queryKeys: ["deal.currentInvestors.slug[]"],
    dataPaths: ["deal.currentInvestors.slug"],
    label: "Current Owners/Investor",
    type: "array",
    appliesTo: ["companies"]
  },
  latestTransactionType: {
    queryKeys: ["transactionHistory.latestTransactionType.slug[]"],
    dataPaths: ["transactionHistory.latestTransactionType.slug"],
    label: "Latest Transaction Type",
    type: "array",
    appliesTo: ["companies"]
  },
  latestFinancingType: {
    queryKeys: ["transactionHistory.latestFinancingType.slug[]"],
    dataPaths: ["transactionHistory.latestFinancingType.slug"],
    label: "Latest Transaction Financing Type",
    type: "array",
    appliesTo: ["companies"]
  },
  appliedFields: {
    queryKeys: ["appliedFields[]"],
    dataPaths: ["appliedFields"],
    label: "Applied Fields",
    type: "array",
    appliesTo: ["companies"]
  },
  limitedPartnerSlug: {
    queryKeys: ["slug[]"],
    dataPaths: ["slug"],
    label: "Limited Partner Name",
    type: "array",
    appliesTo: ["limited_partners"]
  },
  geographicOrigin: {
    queryKeys: ["countryOfOperation.slug[]"],
    dataPaths: ["countryOfOperation.slug"],
    label: "Geographic Origin",
    type: "array",
    appliesTo: ["limited_partners"]
  },
  regionsOfInvestment: {
    queryKeys: ["regionsOfInvestment.slug[]"],
    dataPaths: ["regionsOfInvestment.slug"],
    label: "Geographic Focus",
    type: "array",
    appliesTo: ["limited_partners"]
  },

  totalAmountCommitted: {
    queryKeys: [
      "fund.totalAmountCommitted.min",
      "fund.totalAmountCommitted.max",
      "fund.totalAmountCommitted.type"
    ],
    dataPaths: ["fund.totalAmountCommitted"],
    label: "Amount Committed ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["limited_partners"]
  },

  "commitmentMetrics.noOfCommitmentsInAfricanFund": {
    queryKeys: [
      "commitmentMetrics.noOfCommitmentsInAfricanFund.min",
      "commitmentMetrics.noOfCommitmentsInAfricanFund.max",
      "commitmentMetrics.noOfCommitmentsInAfricanFund.type"
    ],
    dataPaths: ["commitmentMetrics.noOfCommitmentsInAfricanFund"],
    label: "# Commitments in African Funds",
    type: "number",
    isRange: true,
    appliesTo: ["limited_partners"]
  },

  assetsUnderManagement: {
    queryKeys: [
      "fund.assetsUnderManagement.min",
      "fund.assetsUnderManagement.max",
      "fund.assetsUnderManagement.type"
    ],
    dataPaths: ["fund.assetsUnderManagement"],
    label: "Assets Under Management ($M)",
    type: "number",
    isRange: true,
    appliesTo: ["limited_partners"]
  },

  "commitmentMetrics.lastFundCommitmentDate": {
    queryKeys: [
      "commitmentMetrics.lastFundCommitmentDate.predefinedOperation",
      "commitmentMetrics.lastFundCommitmentDate.operationName",
      "commitmentMetrics.lastFundCommitmentDate.start",
      "commitmentMetrics.lastFundCommitmentDate.end"
    ],
    dataPaths: [
      "commitmentMetrics.lastFundCommitmentDate",
      "commitmentMetrics.lastFundCommitmentDate.predefinedOperation",
      "commitmentMetrics.lastFundCommitmentDate.operationName",
      "commitmentMetrics.lastFundCommitmentDate.start",
      "commitmentMetrics.lastFundCommitmentDate.end"
    ],
    label: "Last Fund Commitment Date",
    type: "date",
    appliesTo: ["limited_partners"]
  },

  "commitmentMetrics.lastAfricanFundCommitmentDate": {
    queryKeys: [
      "commitmentMetrics.lastAfricanFundCommitmentDate.predefinedOperation",
      "commitmentMetrics.lastAfricanFundCommitmentDate.operationName",
      "commitmentMetrics.lastAfricanFundCommitmentDate.start",
      "commitmentMetrics.lastAfricanFundCommitmentDate.end"
    ],
    dataPaths: [
      "commitmentMetrics.lastAfricanFundCommitmentDate",
      "commitmentMetrics.lastAfricanFundCommitmentDate.predefinedOperation",
      "commitmentMetrics.lastAfricanFundCommitmentDate.operationName",
      "commitmentMetrics.lastAfricanFundCommitmentDate.start",
      "commitmentMetrics.lastAfricanFundCommitmentDate.end"
    ],
    label: "Last African Fund Commitment Date",
    type: "date",
    appliesTo: ["limited_partners"]
  },

  limitedPartnerType: {
    queryKeys: ["limitedPartnerType.slug[]"],
    dataPaths: ["limitedPartnerType.slug"],
    label: "Limited Partner Type",
    type: "array",
    appliesTo: ["limited_partners"]
  },
  sectorPreferences: {
    queryKeys: ["sectorPreferences.slug[]"],
    dataPaths: ["sectorPreferences.slug"],
    label: "Priority Sectors",
    type: "array",
    appliesTo: ["limited_partners"]
  },

  fundInvestmentStrategies: {
    queryKeys: ["fundInvestmentStrategies.slug[]"],
    dataPaths: ["fundInvestmentStrategies.slug"],
    label: "Investment Approach",
    type: "array",
    appliesTo: ["limited_partners"]
  },

  investmentFundManager: {
    queryKeys: ["investmentFundManager.slug[]"],
    dataPaths: ["investmentFundManager.slug"],
    label: "Investment Fund Manager",
    type: "array",
    appliesTo: ["limited_partners"]
  },

  "commitments.slug": {
    queryKeys: ["commitments.slug[]"],
    dataPaths: ["commitments.slug"],
    label: "Fund Vehicle",
    type: "array",
    appliesTo: ["limited_partners"]
  }
}

export const KNOWN_QUERY_CRITERIA_KEY = Object.values(
  SCREENER_FIELD_CONFIG
).flatMap((f) => f.queryKeys)

export const QUERY_PARAM_TO_DATA_PATH_MAP = Object.values(
  SCREENER_FIELD_CONFIG
).reduce((acc, field) => {
  field.dataPaths.forEach((dataPath) => {
    acc[dataPath] = dataPath
  })
  return acc
}, {} as Record<string, string>)

export const DEALS_SCREENER_PATH_LABEL_HASH_MAP = Object.values(
  SCREENER_FIELD_CONFIG
).reduce((acc, field) => {
  if (field.dataPaths[0] !== "appliedFields") {
    acc[field.dataPaths[0]] = field.label
  }
  return acc
}, {} as Record<string, string>)

export const INVESTMENT_STRATEGY_VC = "venture-capital"
export const VC_FUNDING_ROUND_KEY = "vcFundingRound.slug"

export const FETCH_LIMIT_CAP = 9999
export const ROOT_ITEM_TO_IGNORE_MOBILE = ["private capital", "open data"]

export const DEALS_ARRAY_PROPERTIES = [
  "investors",
  "financingInstruments",
  "transactionTypes",
  "investmentStrategies",
  "vcFundingRounds",
  "company.sectors",
  "company.subsectors",
  "company.countriesOfOperation",
  "sellers"
]

export const FINANCIALS_LABEL_MAP = {
  revenue: "Revenue($M)",
  ebitda: "EBITDA($M)",
  operatingExpenses: "Operating Expenses($M)",
  percentagePriceToEarningRatio: "Price/Earning Ratio($M)",
  priceToBookRatio: "Price/Book Ratio($M)",
  evSales: "EV/Sales",
  evEbitda: "EV/EBITDA",
  grossMarginRatio: "Gross Margin Ratio(%)",
  debtToEquityRatio: "Debt/Equity Ratio(%)",
  percentageNetProfitMargin: "Net Profit Margin(%)",
  percentageReturnOnEquity: "Return on Equity(%)",
  currentRatio: "Current Ratio"
}

export const financialSortingFields: Record<string, string> = {
  "financials.0.revenueUsd": "financial.revenueUsd",
  "financials.0.ebitdaUsd": "financial.ebitdaUsd",
  "financials.0.operatingExpenses": "financial.operatingExpenses",
  "financials.0.percentagePriceToEarningRatio":
    "financial.percentagePriceToEarningRatio",
  "financials.0.evEbitda": "financial.evEbitda",
  "financials.0.evSales": "financial.evSales",
  "financials.0.priceToBookRatio": "financial.priceToBookRatio",
  "financials.0.debtToEquityRatio": "financial.debtToEquityRatio",
  "financials.0.percentageReturnOnEquity": "financial.percentageReturnOnEquity",
  "financials.0.percentageReturnOnAssets": "financial.percentageReturnOnAssets",
  "financials.0.percentageGrossMargin": "financial.percentageGrossMargin",
  "financials.0.percentageNetProfitMargin":
    "financial.percentageNetProfitMargin",
  "financials.0.currentRatio": "financial.currentRatio",
  "financials.0.reportingDate": "financial.reportingDate"
}

export const companySortingFields: Record<string, string> = {
  sectors: "sectors",
  subsectors: "subsectors",
  "transactionHistory.totalAmountRaised":
    "transactionHistory.totalAmountRaised",
  "transactionHistory.lastTransactionAnnouncementDate":
    "transactionHistory.lastTransactionAnnouncementDate",
  "transactionHistory.latestTransactionType":
    "transactionHistory.latestTransactionType",
  "transactionHistory.latestFinancingType":
    "transactionHistory.latestFinancingType",
  "transactionHistory.lastTransactionAmount":
    "transactionHistory.lastTransactionAmount"
}

export const limitedPartnerSortingFields: Record<string, string> = {
  investorTypes: "investorTypes",
  countriesOfOperation: "countriesOfOperation",
  regionsOfInvestment: "regionsOfInvestment",
  sectorPreferences: "sectorPreferences",
  assetsUnderManagement: "assetsUnderManagement",
  totalAmountCommitted: "totalAmountCommitted",
  fundInvestmentStrategies: "fundInvestmentStrategies"
}

export const lPCommitmentMetricsSortingFields: Record<string, string> = {
  noOfCommitmentsInAfricanFund: "noOfCommitmentsInAfricanFund",
  lastFundCommitmentDate: "lastFundCommitmentDate",
  lastAfricanFundCommitmentDate: "lastAfricanFundCommitmentDate"
}

export const genericSortingFields: Record<string, string> = {
  foundingYear: "foundingYear",
  ownershipStatus: "ownershipStatus",
  status: "status"
}

export const QUERY_CRITERIA_BY_TYPE: Record<string, string[]> = {
  recent_transactions: [
    "company.slug",
    "company.tag.slug",
    "countryOfOperation.slug",
    "company.ownershipStatus",
    "deal.dealSize.min",
    "deal.dealSize.max",
    "deal.percentageEquityStake",
    "deal.announcementDate",
    "deal.impliedEv",
    "deal.impliedEvRevenue",
    "deal.impliedEvEbitda",
    "deal.fund",
    "financial.ebitda",
    "financial.revenue",
    "investor.slug",
    "leadInvestor.slug",
    "subsector.slug",
    "transactionType.slug",
    "investmentStrategy.slug",
    "financingInstrument.slug",
    "vcFundingRound.slug",
    "deal.dealSize",
    "sector.slug",
    "market.slug"
  ],
  companies: [
    "company.slug",
    "company.tag.slug",
    "countryOfOperation.slug",
    "company.ownershipStatus",
    "company.status",
    "financial.currentRatio",
    "financial.debtToEquityRatio",
    "transactionHistory.totalAmountRaised",
    "transactionHistory.lastTransactionAmount",
    "transactionHistory.lastPreMoneyValuation",
    "transactionHistory.lastPostMoneyValuation",
    "financial.revenue",
    "financial.ebitda",
    "financial.operatingExpenses",
    "financial.percentagePriceToEarningRatio",
    "financial.priceToBookRatio",
    "financial.evSales",
    "financial.percentageNetProfitMargin",
    "financial.percentageReturnOnEquity",
    "foundingYear.predefinedOperation",
    "foundingYear.selectedOption",
    "foundingYear.operationName",
    "transactionHistory.totalAmountRaised",
    "transactionHistory.lastTransactionAmount",
    "transactionHistory.lastPreMoneyValuation",
    "transactionHistory.lastPostMoneyValuation",
    "foundingYear",
    "deal.currentInvestors.slug",
    "transactionHistory.latestFinancingType.slug",
    "transactionHistory.latestTransactionType.slug",
    "appliedFields[]",
    "sector.slug",
    "market.slug"
  ],
  limited_partners: [
    "slug",
    "company.tag.slug",
    "countryOfOperation.slug",
    "foundingYear",
    "foundingYear.predefinedOperation",
    "foundingYear.selectedOption",
    "foundingYear.operationName",
    "financial.portfolioSize",
    "commitmentMetrics.lastFundCommitmentDate",
    "commitmentMetrics.lastAfricanFundCommitmentDate",
    "fund.assetsUnderManagement",
    "limitedPartnerType.slug",
    "regionsOfInvestment.slug",
    "sectorPreferences.slug",
    "investmentInstrument.slug",
    "commitments.slug",
    "fund.totalAmountCommitted",
    "fundInvestmentStrategies.slug",
    "investmentFundManager.slug",
    "commitmentMetrics.noOfCommitmentsInAfricanFund"
  ]
}

export const cacheFirstPollingOptions = {
  fetchPolicy: "no-cache"
} as QueryHookOptions

export const exportDelayTime = 1000 * 5 // %secs

export const DEALS_AGGREGATE_FIELDS = {
  totalDealAmount: { type: "monetary" },
  percentageEquityStake: { type: "percentage" },
  revenue: { type: "monetary" },
  ebitda: { type: "monetary" },
  impliedEv: { type: "monetary" },
  impliedEvEbitda: { type: "regular" },
  impliedEvRevenue: { type: "regular" }
} as const

export const USE_SEMANTIC_SEARCH_QUERY_PARAM = "use_semantic_search"
